import './Newsletter.scss'

import React, {useState} from 'react'
import {FormProvider, useForm} from 'react-hook-form'
import {WysiwygType} from '../../types'
import {fetcher} from '../../util/fetcher'
import {useLanguage, useTranslation} from '../../util/i18n'
import {style} from '../../util/style'
import {Theme} from '../Theme'
import {
	FieldsubmitField,
	FieldsubmitRow,
	FieldsubmitSubmit
} from '../ui/Fieldsubmit'
import {MarkedTitle} from '../ui/MarkedTitle'
import {Input, Label} from './Form'
import {Errors} from './form/Errors'

const styles = style('newsletter', ['form'])

export type NewsletterType = {
	title?: string
	intro?: WysiwygType
	submitlabel?: string
	audienceId?: string
	inputlabel?: string
}

export const Newsletter: React.FC<NewsletterType> = ({
	title,
	intro,
	submitlabel,
	inputlabel,
	audienceId
}) => {
	const t = useTranslation('newsletter')
	if (!audienceId) return null
	return (
		<div className={styles()}>
			<Theme.Container>
				<MarkedTitle className={styles('title')} title={title || t.title} />
				<div className={styles('content')}>
					{intro && (
						<div className={styles('content-text')}>
							<Theme.Wysiwyg>{intro}</Theme.Wysiwyg>
						</div>
					)}
					<NewsletterForm
						inputLabel={inputlabel}
						submit={submitlabel}
						audienceId={audienceId}
					/>
				</div>
			</Theme.Container>
		</div>
	)
}

const NewsletterForm: React.FC<{
	inputLabel?: string
	submit?: string
	audienceId?: string
}> = ({submit, audienceId, inputLabel}) => {
	const t = useTranslation('newsletter')
	const lang = useLanguage()
	const methods = useForm()
	const {setError, clearErrors, trigger, getValues} = methods
	const [isSending, setIsSending] = useState(false)
	const [isSubmitted, setIsSubmitted] = useState(false)
	const processedId = Array.isArray(audienceId) ? audienceId[0] : audienceId
	const onSubmit = (data) => {
		setIsSending(true)
		const {email} = data
		fetchForMailchimp(
			`/api/subscribetomailchimp?language=${lang}&audienceId=${processedId}&email=${email}`,
			setIsSending,
			setIsSubmitted,
			setError
		)
	}

	const handleFormSubmit = async (e) => {
		e.preventDefault()
		if (isSending) return
		const isValid = await trigger() // trigger validation
		if (!isValid) return

		clearErrors()
		const formData = getValues()
		onSubmit(formData)
	}

	const fieldsubmit = {
		type: 'email',
		name: 'email',
		id: 'email',
		label: inputLabel || 'E-mail'
	}

	return (
		<FormProvider {...methods}>
			{!isSubmitted ? (
				<form className={styles.form()}>
					<FieldsubmitRow>
						<FieldsubmitField>
							<Label
								htmlFor={fieldsubmit.id || fieldsubmit.name}
								label={fieldsubmit.label}
								maxwidth={true}
							/>
							<Input
								{...fieldsubmit}
								id={fieldsubmit.id || fieldsubmit.name}
								required
							/>
						</FieldsubmitField>
						<FieldsubmitSubmit maxwidth={true} nomargin={true}>
							<Theme.Button
								loading={isSending}
								as="button"
								onClick={handleFormSubmit}
								className={styles.form('submit')}
							>
								{submit || 'Submit'}
							</Theme.Button>
						</FieldsubmitSubmit>
					</FieldsubmitRow>
					<Errors />
				</form>
			) : (
				<div className={styles.form('submitted')}>
					<Theme.P>{t.submitted}</Theme.P>
				</div>
			)}
		</FormProvider>
	)
}

export const fetchForMailchimp = (
	url,
	setIsSending,
	setIsSubmitted,
	setError
) => {
	fetcher(url)
		.then((res) => {
			setIsSending(false)
			setIsSubmitted(true)
		})
		.catch((err) => {
			setIsSending(false)
			Object.entries(err).forEach(
				([field, messages]: [string, Array<string>]) => {
					return messages.forEach((m) => {
						setError(field, {type: 'validate', message: m})
					})
				}
			)
		})
}
